import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Award, Users, Trophy, Heart, Shield, Star, ArrowRight } from 'lucide-react';

const HighlightsSection = () => {
  const highlights = [
    {
      icon: Trophy,
      title: "22+ Years of Expertise",
      description: "Over two decades of excellence in physiotherapy and wellness care",
      gradient: "from-amber-400 to-orange-500"
    },
    {
      icon: Award,
      title: "Multiple Awards",
      description: "Recognized for outstanding patient care and medical excellence",
      gradient: "from-blue-400 to-indigo-500"
    },
    {
      icon: Users,
      title: "1,10,000+ Success Stories",
      description: "Thousands of patients restored to optimal health and mobility",
      gradient: "from-emerald-400 to-teal-500"
    },
    {
      icon: Star,
      title: "Trusted by Professionals",
      description: "The preferred choice of athletes, executives, and healthcare leaders",
      gradient: "from-purple-400 to-pink-500"
    },
    {
      icon: Shield,
      title: "Certified Excellence",
      description: "All practitioners hold advanced certifications and credentials",
      gradient: "from-rose-400 to-red-500"
    },
    {
      icon: Heart,
      title: "Patient-First Philosophy",
      description: "Every treatment plan is personalized for optimal outcomes",
      gradient: "from-cyan-400 to-blue-500"
    }
  ];

  const navigate = useNavigate();
  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent"></div>
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#204080]/5 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#204080] to-[#3a5bb8] drop-shadow-sm mb-4 tracking-tight leading-snug font-serif">
            Why Choose Visakha Physiotherapy & Jasti Chiropractic?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Experience the pinnacle of physiotherapy care with our award-winning team, 
            state-of-the-art facilities, and unwavering commitment to your wellness journey.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {highlights.map((highlight, index) => {
            const IconComponent = highlight.icon;
            return (
              <Card key={index} className="group relative overflow-hidden p-8 bg-white/80 backdrop-blur-sm border border-gray-200 hover:border-[#204080]/30 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                {/* Icon Container */}
                <div className="relative mb-6">
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${highlight.gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent size={32} className="text-white" />
                  </div>
                  {/* Floating background effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${highlight.gradient} rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300`}></div>
                </div>

                <h3 className="text-xl font-bold text-[#204080] mb-4 group-hover:text-[#2E5AAC] transition-colors duration-300">
                  {highlight.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {highlight.description}
                </p>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#204080]/5 to-[#2E5AAC]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Bottom accent line */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${highlight.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 cursor-pointer"
            onClick={() => navigate('/book-appointment')}
            role="button"
            tabIndex={0}
            style={{ userSelect: 'none' }}
          >
            <span className="font-semibold">Ready to Experience Excellence?</span>
            <ArrowRight size={20} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HighlightsSection;